import type { PastAnnualRevenueEarnings } from '@/pages/Narrative/types';
import {
  calculateFutureRevenueAndEarnings,
  calculateProfitMarginPercentage,
} from '../lib';
import { useMemo } from 'react';

type PlotData = Record<number, { netIncome: number; revenue: number }>;

export type Props = {
  profitMarginPercentage: number;
  revenuePercentage: number;
  timePeriod: number;
  pastAnnualRevenueEarnings: PastAnnualRevenueEarnings;
};

const byDescendingYear = (a: number, b: number) => b - a;

export const useTimeseriesData = ({
  profitMarginPercentage,
  revenuePercentage,
  timePeriod,
  pastAnnualRevenueEarnings,
}: Props) => {
  const mostRecentReportedYear = new Date(
    pastAnnualRevenueEarnings.mostRecentReportedDate
  ).getFullYear();

  const revenue =
    pastAnnualRevenueEarnings?.yearlyTimeSeries?.[mostRecentReportedYear]
      ?.revenue ?? 0;

  const reportingUnit = 1;

  const { pastTimeRange, pastPlotData } = useMemo(() => {
    const pastTimeRange = Object.keys(
      pastAnnualRevenueEarnings?.yearlyTimeSeries || {}
    )
      .map(Number)
      .sort(byDescendingYear);

    return {
      pastTimeRange,
      pastPlotData: pastAnnualRevenueEarnings?.yearlyTimeSeries,
    };
  }, [pastAnnualRevenueEarnings?.yearlyTimeSeries]);

  const { futureTimeRange, futurePlotData } = useMemo(() => {
    const addOneYearPerIndex = (_, index: number) =>
      mostRecentReportedYear + index;

    const futureTimeRange = new Array(timePeriod + 1)
      .fill(0)
      .map(addOneYearPerIndex);

    const futurePlotData = futureTimeRange.reduce<PlotData>((result, year) => {
      const currentYearData = pastPlotData[mostRecentReportedYear];

      if (year === mostRecentReportedYear && currentYearData) {
        return {
          ...result,
          [year]: currentYearData,
        };
      }

      const profitMarginPercentageAtYear = calculateProfitMarginPercentage({
        timePeriod,
        futureProfitMarginPercentage: profitMarginPercentage,
        year: year - mostRecentReportedYear,
        presentProfitMarginPercentage: currentYearData
          ? (currentYearData.netIncome / currentYearData.revenue) * 100
          : 0,
      });

      const { futureNetIncome, futureRevenue } =
        calculateFutureRevenueAndEarnings({
          timePeriod: year - mostRecentReportedYear,
          profitMarginPercentage: profitMarginPercentageAtYear,
          reportingUnit,
          revenue: revenue,
          revenuePercentage,
        });

      return {
        ...result,
        [year]: { revenue: futureRevenue, netIncome: futureNetIncome },
      };
    }, {});

    return {
      futureTimeRange,
      futurePlotData,
    };
  }, [
    mostRecentReportedYear,
    profitMarginPercentage,
    reportingUnit,
    revenue,
    revenuePercentage,
    timePeriod,
    pastPlotData,
  ]);

  return useMemo(() => {
    // Plot data of past and future projections consumed by the chart
    const plotData = {
      ...(pastPlotData as PlotData),
      ...futurePlotData,
    };

    // Derive time range
    const timeRange = Array.from(
      new Set([...pastTimeRange, ...futureTimeRange])
    ).sort();

    const valueRange = Object.values(plotData).reduce<number[]>(
      (acc, { netIncome, revenue }) => [...acc, revenue, netIncome],
      [0]
    );

    return {
      pastTimeRange,
      futureTimeRange,
      timeRange,
      valueRange,
      mostRecentReportedYear,
      plotData,
    };
  }, [
    mostRecentReportedYear,
    futurePlotData,
    futureTimeRange,
    pastPlotData,
    pastTimeRange,
  ]);
};
